import { useEffect, useState } from "react";
import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import CloudSyncOutlinedIcon from "@mui/icons-material/CloudSyncOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";

import { getActiveProjectId } from "src/handlers/Projects";
import { formatRelative } from "src/handlers/formatDate";
import useSyncStatus from "src/hooks/useSyncStatus";

// Phase 2.1 + tweak — subtle save-state indicator.
//
// Renders a small icon + muted-gray label instead of a filled chip. The whole
// surface has no border or background so it visually recedes next to the
// project switcher and user menu.

export default function SaveStatusChip({ sx }) {
  const { status, lastSavedAt } = useSyncStatus();
  const [, forceRerender] = useState(0);
  const [hasProject, setHasProject] = useState(() => Boolean(getActiveProjectId()));

  useEffect(() => {
    const refresh = () => setHasProject(Boolean(getActiveProjectId()));
    window.addEventListener("storage", refresh);
    window.addEventListener("projectSwitched", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("projectSwitched", refresh);
    };
  }, []);

  useEffect(() => {
    if (status !== "saved" || !lastSavedAt) return undefined;
    const handle = window.setInterval(() => forceRerender((n) => n + 1), 30_000);
    return () => window.clearInterval(handle);
  }, [status, lastSavedAt]);

  if (!hasProject) return null;

  let Icon = CloudDoneOutlinedIcon;
  let label = "Saved";
  let tooltip = "All changes are synced to the cloud.";
  let isError = false;

  if (status === "dirty") {
    Icon = CloudSyncOutlinedIcon;
    label = "Unsaved";
    tooltip = "Changes are queued locally. They will sync in a moment.";
  } else if (status === "saving") {
    Icon = null; // replaced with a spinner below
    label = "Saving…";
    tooltip = "Syncing your changes to the cloud.";
  } else if (status === "saved") {
    Icon = CheckRoundedIcon;
    label = lastSavedAt ? `Saved ${formatRelative(lastSavedAt)}` : "Saved";
    tooltip = "All changes are synced to the cloud.";
  } else if (status === "error") {
    Icon = ErrorOutlineOutlinedIcon;
    label = "Sync failed";
    tooltip = "The last write failed. Your edits are safe locally — we will keep retrying.";
    isError = true;
  }

  if (typeof navigator !== "undefined" && !navigator.onLine) {
    Icon = CloudOffOutlinedIcon;
    label = "Offline";
    tooltip = "You are offline. Changes are saved locally and will sync automatically when you reconnect.";
  }

  return (
    <Tooltip title={tooltip} arrow>
      <Box
        component="span"
        sx={(theme) => ({
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          px: 0.5,
          color: isError ? theme.palette.error.main : theme.palette.text.secondary,
          opacity: isError ? 1 : 0.75,
          fontSize: 12,
          lineHeight: 1,
          ...sx,
        })}
      >
        {status === "saving" ? (
          <CircularProgress size={12} thickness={6} sx={{ color: "inherit" }} />
        ) : (
          Icon ? <Icon sx={{ fontSize: 14, color: "inherit" }} /> : null
        )}
        <Typography
          variant="caption"
          sx={{
            color: "inherit",
            fontWeight: 500,
            letterSpacing: 0.1,
            textTransform: "none",
            fontSize: 12,
            lineHeight: 1,
          }}
        >
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
}
